import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Sidebar } from '../components/Sidebar';
import { EditAlertView } from './EditAlertView';
import { Alert, AlertFormData, User, SearchResult, PriceAnalysis } from '../types/alert.types';
import { buildFinalVintedUrl } from '../hooks/useAlertForm';
import { API_BASE_URL } from '../api/apiClient';

export const EditAlertPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [user, setUser] = useState<User | null>(null);
  const [alertData, setAlertData] = useState<Alert | null>(null);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [analysis, setAnalysis] = useState<PriceAnalysis | null>(null);
  const [loading, setLoading] = useState(true);

  // Charger les informations utilisateur
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/auth/me`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          setUser(data);
        }
      } catch (error) {
        console.error('Erreur chargement utilisateur:', error);
      }
    };
    fetchUser();
  }, []);

  // Charger l'alerte à éditer
  useEffect(() => {
    const fetchAlert = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/alerts/${id}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          setAlertData(data.alert || data);
          setResults(data.results || []);
          setAnalysis(data.analysis || null);
        } else {
          console.error('Erreur chargement alerte');
          navigate('/app');
        }
      } catch (error) {
        console.error('Erreur:', error);
        navigate('/app');
      } finally {
        setLoading(false);
      }
    };

    fetchAlert();
  }, [id, navigate]);

  const handleSave = async (formData: AlertFormData) => {
    if (!id) return;

    try {
      const vintedUrl = buildFinalVintedUrl(formData);

      // Transformer les données du format camelCase au format snake_case attendu par le backend
      const alertData = {
        name: formData.name,
        vinted_url: vintedUrl,
        category_id: formData.categoryId || null,
        category_path: formData.categoryPath || null,
        selected_conditions: formData.selectedConditions.length > 0 ? formData.selectedConditions : null,
        selected_statuses: (formData.selectedStatuses && formData.selectedStatuses.length > 0) ? formData.selectedStatuses : null,
        selected_sizes: formData.selectedSizes.length > 0 ? formData.selectedSizes.map(s => ({ id: s.id, title: s.title })) : null,
        selected_colors: formData.selectedColors.length > 0 ? formData.selectedColors : null,
        selected_brands: formData.selectedBrands.length > 0 ? formData.selectedBrands.map(b => ({ id: b.id, title: b.title })) : null,
        selected_materials: (formData.selectedMaterials && formData.selectedMaterials.length > 0) ? formData.selectedMaterials.map(m => ({ id: m.id, title: m.title })) : null,
        selected_patterns: (formData.selectedPatterns && formData.selectedPatterns.length > 0) ? formData.selectedPatterns.map(p => ({ id: p.id, title: p.title })) : null,
        selected_languages: (formData.selectedLanguages && formData.selectedLanguages.length > 0) ? formData.selectedLanguages.map(l => l.id) : null,
        selected_platforms: (formData.selectedPlatforms && formData.selectedPlatforms.length > 0) ? formData.selectedPlatforms.map(p => ({ id: p.id, title: p.title })) : null,
        selected_ratings: (formData.selectedRatings && formData.selectedRatings.length > 0) ? formData.selectedRatings.map(r => ({ id: r.id, title: r.title })) : null,
        min_price: formData.minPrice ? parseFloat(formData.minPrice) : null,
        max_price: formData.maxPrice ? parseFloat(formData.maxPrice) : null,
        min_likes: formData.minLikes ? parseInt(formData.minLikes) : null,
        max_likes: formData.maxLikes ? parseInt(formData.maxLikes) : null,
        max_pages: parseInt(formData.maxPages, 10) || 5,
        trending_only: formData.trendingOnly || false,
        must_include: formData.mustInclude.length > 0 ? formData.mustInclude : null,
        must_exclude: formData.mustExclude.length > 0 ? formData.mustExclude : null
      };

      console.log('Modification alerte - données envoyées:', alertData);

      const response = await fetch(`${API_BASE_URL}/alerts/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify(alertData)
      });

      if (response.ok) {
        navigate('/app');
      } else {
        const errorData = await response.json();
        console.error('Erreur modification alerte:', errorData);
        window.alert(`Erreur lors de la modification de l'alerte: ${errorData.detail || 'Erreur inconnue'}`);
      }
    } catch (error) {
      console.error('Erreur:', error);
      window.alert('Erreur lors de la modification de l\'alerte');
    }
  };

  const handleBack = () => {
    navigate('/app');
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    navigate('/login');
  };

  if (loading || !alertData) {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar
          user={user}
          currentPage="alerts"
          onLogout={handleLogout}
          onPageChange={(page) => {
            if (page === 'alerts') navigate('/app');
          }}
        />
        <main className="flex-1 ml-64 h-screen flex items-center justify-center">
          <div className="text-gray-600">Chargement...</div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar gauche réutilisable */}
      <Sidebar
        user={user}
        currentPage="alerts"
        onLogout={handleLogout}
        onPageChange={(page) => {
          if (page === 'alerts') navigate('/app');
        }}
      />

      {/* Contenu principal */}
      <main className="flex-1 ml-64 h-screen flex flex-col overflow-auto">
        <div className="p-8">
          <div className="max-w-7xl mx-auto">
            {/* Header avec titre */}
            <div className="mb-6">
              <h1 className="text-3xl font-bold bg-gradient-to-r from-cyan-600 to-teal-600 bg-clip-text text-transparent">
                Modifier l'alerte
              </h1>
              <p className="text-gray-600 mt-2">
                Modifiez les paramètres de votre alerte "{alertData.name}"
              </p>
            </div>

            {/* Composant d'édition */}
            <EditAlertView
              alert={alertData}
              onBack={handleBack}
              onSave={handleSave}
              results={results}
              analysis={analysis}
            />
          </div>
        </div>
      </main>
    </div>
  );
};
